import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './MyStudents.css';

const MyStudents = ({ userData }) => {
  const [activeTab, setActiveTab] = useState('active');
  const [students, setStudents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchStudents = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/teacher/mystudents/${userData.id}`
        );
        setStudents(response.data.students);
      } catch (err) {
        setError('Failed to fetch students. Please try again later.');
        console.error('Error fetching students:', err);
      } finally {
        setIsLoading(false);
      }
    };

    if (userData) {
      fetchStudents();
    }
  }, [userData]);

  const handleStudentClick = (studentId) => {
    window.open(`/StudentProfile/${studentId}`, '_blank');
  };

  const getStatusClass = (sessionsTaken, totalSessions) => {
    const remaining = totalSessions - sessionsTaken;
    if (remaining === 1) return 'student-sessions-remaining critical';
    if (remaining === 2) return 'student-sessions-remaining warning';
    return 'student-sessions-remaining';
  };

  const activeStudents = students.filter(student => student.totalSessions > student.sessionsTaken);
  const inactiveStudents = students.filter(student => student.totalSessions === student.sessionsTaken);

  if (isLoading || !userData) {
    return (
      <div className="Mystudents-container">
        <div className="students-loading">
          <div className="loading-spinner"></div>
          <p>Loading your students...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="Mystudents-container">
      <header className="students-header">
        <div className="header-content">
          <h1>
            My Students <span>Management</span>
          </h1>
          <p className="header-subtitle">Track and manage your students</p>
        </div>
      </header>

      <div className="students-tabs">
        <button
          className={`tab-button ${activeTab === 'active' ? 'active' : ''}`}
          onClick={() => setActiveTab('active')}
        >
          Active Students
          <span className="tab-count">{activeStudents.length}</span>
        </button>
        <button
          className={`tab-button ${activeTab === 'inactive' ? 'active' : ''}`}
          onClick={() => setActiveTab('inactive')}
        >
          Inactive Students
          <span className="tab-count">{inactiveStudents.length}</span>
        </button>
      </div>

      {error && <div className="students-error">{error}</div>}

      <div className="students-grid">
        {activeTab === 'active' ? (
          activeStudents.length > 0 ? (
            activeStudents.map((student) => (
              <div 
                key={student.IdUser} 
                className="student-card"
                onClick={() => handleStudentClick(student.IdUser)}
              >
                <div className="student-avatar">
                  <img 
                    src={student.Image} 
                    alt={`${student.FirstName} ${student.LastName}`}
                  />
                </div>
                <div className="student-info">
                  <h3>{student.FirstName} {student.LastName}</h3>
                  <p className="student-meta">Grade {student.Grade} • Age {student.Age}</p>
                  <div className={getStatusClass(student.sessionsTaken, student.totalSessions)}>
                    {student.totalSessions - student.sessionsTaken} sessions left
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="empty-state">
              <div className="empty-icon">👋</div>
              <h3>No active students</h3>
              <p>You currently don't have any active students</p>
            </div>
          )
        ) : (
          inactiveStudents.length > 0 ? (
            inactiveStudents.map((student) => (
              <div 
                key={student.IdUser} 
                className="student-card inactive"
                onClick={() => handleStudentClick(student.IdUser)}
              >
                <div className="student-avatar">
                  <img 
                    src={student.Image} 
                    alt={`${student.FirstName} ${student.LastName}`}
                  />
                </div>
                <div className="student-info">
                  <h3>{student.FirstName} {student.LastName}</h3>
                  <p className="student-meta">Grade {student.Grade} • Age {student.Age}</p>
                  <p className="student-completed">Inactive</p>
                </div>
              </div>
            ))
          ) : (
            <div className="empty-state">
              <div className="empty-icon">🎉</div>
              <h3>No inactive students</h3>
              <p>All your students are currently active</p>
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default MyStudents;